import React, {useState, useEffect} from 'react';
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import { setDisplayTermsPopup, setHasAcceptedTerms } from '../../../state/slices/user';
import apiClient from '../../../auth/apiClient';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
} from '@material-ui/core';

import { useStyles } from './styles';
import UserService from '../../../services/UserService';
import useCurrentFacility from '../../../hooks/useCurrentFacility';
import { useAppActionContext } from '../../../providers/AppStore/AppActionProvider';

//added dummy temrs content for qa testing, this will be removed after completing AO-28810
const dummyTerms = `Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nulla facilisi.
Fusce egestas orci non neque gravida, vitae tincidunt justo pulvinar.
Aliquam erat volutpat. Phasellus posuere sapien ut erat facilisis feugiat.
Suspendisse id urna eu augue cursus pulvinar sit amet sed lorem.
Aenean tincidunt ipsum quis sem tristique, at pellentesque ipsum varius.
Curabitur condimentum sapien non justo posuere, nec placerat velit dapibus.
Nullam tempus mi id urna dapibus venenatis.
Mauris mollis nibh vitae sapien faucibus, vel aliquet est cursus.
Etiam vel felis quam. Integer dictum lectus id lorem lacinia faucibus.
Vivamus non mauris a ipsum convallis tincidunt.
Sed sed tellus sodales, euismod neque ut, sagittis lectus.
Duis lobortis neque a diam interdum, vel egestas tortor feugiat.
Sed faucibus justo nec lorem feugiat varius.
Morbi dapibus purus vel odio condimentum, vitae semper mi varius.
Ut quis nibh et arcu fermentum interdum.
Integer auctor eros eget lacus facilisis luctus.
Pellentesque vulputate metus sed libero viverra, at laoreet felis sodales.
Vivamus cursus tellus sed velit consectetur, vitae mattis orci sollicitudin.
Suspendisse viverra enim non eros ultricies, a interdum turpis feugiat.
Nunc fermentum risus eget augue ultrices porta.`;


export const TermsDialog = ({
  termsTitle = 'Terms & Conditions',
  termsContent = dummyTerms,
  checkboxLabel = 'I agree to the terms and conditions',
  acceptLabel = 'Accept and Continue',
  cancelLabel = 'Close',
  disableBackdropClose = true,
  disableEscClose = false,
}) => {
  const userService = new UserService(apiClient);
  const classes = useStyles();
  const dispatch = useDispatch();
  const isGetReportRoute = window.location.pathname.includes("getreport")
  const [accepted, setAccepted] = useState(false);
  const [daysLeft, setDaysLeft] = useState(30);
  const [daysLabel, setDaysLabel] = useState('');
  const { logOut } = useAppActionContext();
  const currentUser = useSelector((state) => state.user, shallowEqual);
  const { facilityID } = useCurrentFacility();
  const scopeAwareFacilityID = useSelector((state) => state.entityScope?.facilityGroupId || facilityID);
  const creationDate = currentUser?.termsDetails?.creationDate;

  /*
  // this code will be usable once we implement AO-28810
  const fetchTermsContent = async () => {
    try {
      const response = await userService.getTermsContent(currentUser?.UserID, scopeAwareFacilityID)
      console.log(response);
    }catch(e) {
      console.log(e);
    }
  }

  
  useEffect(() => {
    fetchTermsContent();
  }, [])
  */

  const calculateDaysLeft = (date) => {
    if (!date) return 0;
    const creation = new Date(date);
    if (isNaN(creation)) return 0;
    const expiry = new Date(creation.getTime() + 30 * 24 * 60 * 60 * 1000);
    const now = new Date();
    const diffMs = expiry - now;
    return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
  };

  useEffect(() => {
    if(creationDate) setDaysLeft(calculateDaysLeft(creationDate))
  }, [creationDate])

  useEffect(() => {   
    const cappedDays = Math.max(0, Math.min(daysLeft, 30));
    setDaysLabel(
      cappedDays === 1 ? "1 day" : `${cappedDays} days`
    );
  }, [daysLeft])

  const handleClose = (event, reason) => {
    if (disableBackdropClose && reason === 'backdropClick') return;
    if (disableEscClose && reason === 'escapeKeyDown') return;
    dispatch(setDisplayTermsPopup(false));
    setAccepted(false);
    if(!currentUser?.hasAcceptedTerms && daysLeft <=0 ) {
      logOut()
    }
  };

  const handleAccept = async () => {
    try {
      const response = await userService.acceptLatestTerms(currentUser?.UserID, scopeAwareFacilityID, currentUser?.termsDetails?.version);
      dispatch(setDisplayTermsPopup(false));
      dispatch(setHasAcceptedTerms(true));
      setAccepted(false);
    } catch(e) {
      console.error(e);
    }
  }
  
  return (
    <>
      {currentUser?.termsDetails?.version && !isGetReportRoute &&
        <Dialog
          open={currentUser?.displayTermsPopup}
          onClose={handleClose}
          aria-labelledby="terms-dialog-title"
          fullWidth
          maxWidth="sm"
          classes={{ paper: classes.paper }}
        >
          <DialogTitle id="terms-dialog-title" className={classes.title}>
            {termsTitle}
          </DialogTitle>

          <DialogContent dividers className={classes.content}>
            <div className={classes.outer}>
              <div
                className={classes.scrollBox}
                role="document"
                aria-label="Terms content"
                tabIndex={0}
              >
                {typeof termsContent === 'string' ? (
                  <Typography variant="body2" component="div" style={{ whiteSpace: 'pre-wrap' }}>
                    {termsContent}
                  </Typography>
                ) : (
                  termsContent
                )}
              </div>

              {!currentUser?.hasAcceptedTerms ?
                <>
                <div className={classes.controlsRow}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={accepted}
                      onChange={(e) => setAccepted(e.target.checked)}
                      inputProps={{ 'aria-label': checkboxLabel }}
                    />
                  }
                  label={checkboxLabel}
                />

                <div className={classes.buttonsRow}>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!accepted}
                    onClick={handleAccept}
                  >
                    {acceptLabel}
                  </Button>
                  <Button variant="outlined" onClick={handleClose}>
                    {cancelLabel}
                  </Button>
                </div>
              </div>

              <Typography variant="body2" className={classes.footer}>
                {daysLeft >= 1
                  ? `You have ${daysLabel} left to use the platform.`
                  : 'Your access has expired. To continue using the platform, please review and accept the terms and conditions.'}
              </Typography>

              </> : 
              <div className={classes.controlsRow}>
                <div className={classes.buttonsRow}>
                  <Button variant="outlined" onClick={handleClose}>
                    {cancelLabel}
                  </Button>
                </div>
              </div>
              }
            </div>
          </DialogContent>
        </Dialog>
      }
      </>
  );
}

export default TermsDialog